import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useCallback,
    useContext,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import {
    HorizontalFlex,
    HighlightedSection,
    PadContainer,
    Button,
} from 'smartsuite-ui';
import TimeAndPrecisionSelector from './TimeAndPrecisionSelector';
import Filters from './Filters';
import { useLabelContext } from '../hooks/labelContext';
import {
    requiredFiltersHaveValues,
    areWhereClausesValid,
    isRecordLimitValid,
    areTimeAndPrecisionFieldsValid,
} from './filtersModalUtils';
import MeasureFilters from './MeasureFilters';
import DashboardContext from '../hooks/dashboardContext';
import RecordLimit from './RecordLimit';
import { inputTypes } from '../constants/constants';

const DashboardFiltersModal = forwardRef((props, ref) => {
    const { dashboardLabels, translationLabels, interpolateString } =
        useLabelContext();

    const { selectOptions } = useContext(DashboardContext);

    const [updatedTimeAndPrecision, setUpdatedTimeAndPrecision] = useState(
        props.timeAndPrecision
    );
    const [updatedFilterValues, setUpdatedFilterValues] = useState(
        props.filterValues
    );
    const [updatedWhereClauses, setUpdatedWhereClauses] = useState(
        props.whereClauses
    );
    const [updatedRecordLimit, setUpdatedRecordLimit] = useState(
        props.recordLimit
    );

    /* eslint-disable complexity */
    const isStateInvalid = useCallback(
        () =>
            !areTimeAndPrecisionFieldsValid(
                updatedTimeAndPrecision,
                props.timeAndPrecisionRangeDomains,
                props.showPrecisionConfig,
                interpolateString,
                dashboardLabels
            ) ||
            !requiredFiltersHaveValues(
                props.requiredFilterKeys,
                updatedFilterValues,
                props.availableFilters,
                selectOptions
            ) ||
            !areWhereClausesValid(updatedWhereClauses) ||
            !isRecordLimitValid(updatedRecordLimit),
        [
            interpolateString,
            dashboardLabels,
            props.availableFilters,
            props.requiredFilterKeys,
            props.showPrecisionConfig,
            props.timeAndPrecisionRangeDomains,
            selectOptions,
            updatedFilterValues,
            updatedRecordLimit,
            updatedTimeAndPrecision,
            updatedWhereClauses,
        ]
    );

    function getUpdatedTimeAndPrecisionToSave() {
        if (updatedTimeAndPrecision.timeAndPrecisionType === 'next') {
            const startDateTime = moment()
                .startOf('day')
                .format('YYYY-MM-DD HH:mm');
            const endDateTime = moment()
                .add(
                    updatedTimeAndPrecision.rangeValue,
                    updatedTimeAndPrecision.rangeType
                )
                .startOf('day')
                .format('YYYY-MM-DD HH:mm');
            return {
                ...updatedTimeAndPrecision,
                startDateTime,
                endDateTime,
            };
        }
        if (updatedTimeAndPrecision.timeAndPrecisionType === 'last') {
            const startDateTime = moment()
                .subtract(
                    updatedTimeAndPrecision.rangeValue,
                    updatedTimeAndPrecision.rangeType
                )
                .startOf('day')
                .format('YYYY-MM-DD HH:mm');
            const endDateTime = moment()
                .startOf('day')
                .format('YYYY-MM-DD HH:mm');
            return {
                ...updatedTimeAndPrecision,
                startDateTime,
                endDateTime,
            };
        }
        if (updatedTimeAndPrecision.timeAndPrecisionType === 'predefined') {
            const startDateTime = moment()
                .startOf('day')
                .add(
                    updatedTimeAndPrecision.predefinedValue,
                    updatedTimeAndPrecision.predefinedType
                );
            const endDateTime = moment(startDateTime).add(
                updatedTimeAndPrecision.predefinedLength,
                updatedTimeAndPrecision.predefinedType
            );
            return {
                ...updatedTimeAndPrecision,
                startDateTime: startDateTime.format('YYYY-MM-DD HH:mm'),
                endDateTime: endDateTime.format('YYYY-MM-DD HH:mm'),
            };
        }
        return updatedTimeAndPrecision;
    }

    function saveTimeAndPrecisionToLocalStorage() {
        const timeAndPrecision = getUpdatedTimeAndPrecisionToSave();
        localStorage.setItem(
            updatedTimeAndPrecision.lastUsedValueKey ?? 'timeAndPrecision',
            JSON.stringify(timeAndPrecision)
        );
    }

    function saveRememberLastValueFilters() {
        if (props.timeAndPrecision.useRememberLastUsedValue) {
            saveTimeAndPrecisionToLocalStorage();
        }

        if (Object.keys(props.availableFilters).length) {
            const rememberFilterKeys = Object.keys(
                props.availableFilters
            ).filter(
                (key) => props.availableFilters[key].useRememberLastUsedValue
            );
            rememberFilterKeys.forEach((key) => {
                if (props.availableFilters[key].type === inputTypes.string) {
                    localStorage.setItem(
                        props.availableFilters[key].lastUsedValueKey ?? key,
                        updatedFilterValues[key]?.values?.[0] ?? ''
                    );
                } else {
                    localStorage.setItem(
                        props.availableFilters[key].lastUsedValueKey ?? key,
                        JSON.stringify(
                            updatedFilterValues[key]?.values?.map(
                                (value) => value.valueField
                            ) ?? []
                        )
                    );
                }
            });
        }
    }

    function isModified() {
        return (
            !_.isEqual(props.timeAndPrecision, updatedTimeAndPrecision) ||
            !_.isEqual(props.filterValues, updatedFilterValues) ||
            !_.isEqual(props.whereClauses, updatedWhereClauses) ||
            !_.isEqual(props.recordLimit, updatedRecordLimit)
        );
    }
    /* eslint-enable complexity */

    function handleApply() {
        saveRememberLastValueFilters(
            updatedTimeAndPrecision,
            updatedFilterValues
        );
        props.handleSave(
            updatedTimeAndPrecision,
            updatedFilterValues,
            updatedWhereClauses,
            updatedRecordLimit
        );

        if (props.collapseOnApply) {
            props.collapseFilters();
        }
    }

    useImperativeHandle(ref, () => ({
        getCurrentState: () => ({
            updatedTimeAndPrecision,
            updatedFilterValues,
            updatedWhereClauses,
            updatedRecordLimit,
        }),
        unappliedFiltersExist: () => isModified(),
    }));

    useEffect(() => {
        setUpdatedTimeAndPrecision(props.timeAndPrecision);
        setUpdatedFilterValues(props.filterValues);
        setUpdatedWhereClauses(props.whereClauses);
        setUpdatedRecordLimit(props.recordLimit);
    }, [
        props.timeAndPrecision,
        props.filterValues,
        props.whereClauses,
        props.recordLimit,
    ]);

    useEffect(() => {
        props.setIsSaveDisabled(isStateInvalid());
    }, [
        isStateInvalid,
        props,
        updatedTimeAndPrecision,
        updatedFilterValues,
        updatedWhereClauses,
        updatedRecordLimit,
    ]);

    return (
        <div className="page-filters">
            <HighlightedSection>
                <TimeAndPrecisionSelector
                    isGauge={!props.showPrecisionConfig}
                    precisionTypeOptions={props.precisionTypeOptions}
                    predefinedDateRanges={props.predefinedDateRanges}
                    saveTimeAndPrecision={setUpdatedTimeAndPrecision}
                    showPrecisionConfig={props.showPrecisionConfig}
                    showTimeAndPrecision={props.showTimeAndPrecision}
                    timeAndPrecision={updatedTimeAndPrecision}
                    timeAndPrecisionTypeOptions={
                        props.timeAndPrecisionTypeOptions
                    }
                    timeAndPrecisionRangeDomains={
                        props.timeAndPrecisionRangeDomains
                    }
                />
                <Filters
                    additionalFilterKeys={props.additionalFilterKeys}
                    availableFilters={props.availableFilters}
                    filterValues={updatedFilterValues}
                    requiredFilterKeys={props.requiredFilterKeys}
                    saveFilters={setUpdatedFilterValues}
                />
                <MeasureFilters
                    legendFormatter={props.legendFormatter}
                    measures={props.whereClauseOptions}
                    saveWhereClauses={setUpdatedWhereClauses}
                    showMeasureFilters={props.showMeasureFilters}
                    whereClauses={[...updatedWhereClauses, { values: [''] }]}
                />
                <RecordLimit
                    showRecordLimit={props.showRecordLimit}
                    recordLimitConfig={updatedRecordLimit}
                    saveRecordLimit={setUpdatedRecordLimit}
                />
                <PadContainer cssClassModifiers={['pad-medium']}>
                    <HorizontalFlex cssClassModifiers={['justify-flex-end']}>
                        <Button
                            onClick={handleApply}
                            disabled={isStateInvalid()}
                        >
                            {translationLabels.apply}
                        </Button>
                    </HorizontalFlex>
                </PadContainer>
            </HighlightedSection>
        </div>
    );
});

DashboardFiltersModal.propTypes = {
    additionalFilterKeys: PropTypes.instanceOf(Object).isRequired,
    availableFilters: PropTypes.instanceOf(Object),
    collapseFilters: PropTypes.func.isRequired,
    collapseOnApply: PropTypes.bool,
    filterValues: PropTypes.instanceOf(Object).isRequired,
    handleSave: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    precisionTypeOptions: PropTypes.instanceOf(Object),
    predefinedDateRanges: PropTypes.instanceOf(Array).isRequired,
    recordLimit: PropTypes.instanceOf(Object).isRequired,
    requiredFilterKeys: PropTypes.instanceOf(Array).isRequired,
    setIsSaveDisabled: PropTypes.func.isRequired,
    showMeasureFilters: PropTypes.bool.isRequired,
    showPrecisionConfig: PropTypes.bool.isRequired,
    showRecordLimit: PropTypes.bool.isRequired,
    showTimeAndPrecision: PropTypes.bool.isRequired,
    timeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    timeAndPrecisionTypeOptions: PropTypes.instanceOf(Array),
    timeAndPrecisionRangeDomains: PropTypes.instanceOf(Object),
    whereClauses: PropTypes.instanceOf(Array).isRequired,
    whereClauseOptions: PropTypes.instanceOf(Array).isRequired,
};

DashboardFiltersModal.defaultProps = {
    availableFilters: {},
    timeAndPrecisionTypeOptions: undefined,
    collapseOnApply: true,
};

export default DashboardFiltersModal;
